import React, { useState, useEffect } from 'react';
import { getUnreadNotificationsCount, subscribeToNotifications } from '../../lib/notifications';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface NotificationBadgeProps {
  type?: 'message' | 'follow_request';
}

export function NotificationBadge({ type }: NotificationBadgeProps) {
  const [count, setCount] = useState(0);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const loadAndSubscribe = async () => {
      await loadCount();
    
      let subscription;
      if (type === 'message') {
        subscription = supabase.channel('direct_messages_' + user.id)
          .on(
            'postgres_changes',
            {
              event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
              schema: 'public',
              table: 'direct_messages',
              filter: `receiver_id=eq.${user.id}`
            },
            () => {
              loadCount();
            }
          )
          .subscribe();
      } else {
        // Subscribe to notifications for other types
        subscription = subscribeToNotifications(user, (notification) => {
          if (!type || notification.type === type) {
            loadCount();
          }
        });
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    };

    loadAndSubscribe();
  }, [type, user]);

  const loadCount = async () => {
    try {
      setLoading(true);
      const count = await getUnreadNotificationsCount(type); // This already checks for unread messages
      setCount(count);
    } catch (error) {
      console.error('Error loading notification count:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || count === 0) return null;

  return (
    <div className="bg-accent-primary text-white text-xs min-w-[1.25rem] h-5 flex items-center justify-center rounded-full">
      {count}
    </div>
  );
}